import { useCallback, useEffect, useState } from 'react';
import { getQueueFacets, type QueueFilters } from '@/services/contentReviewService';
import type { FacetCounts } from './facetFilters';

/**
 * task-8xq63qswed round 2 (codex-reviewer minor @bcef1aded): the facet counts follow the filters exactly (same
 * object, same serialiser as the queue), and a FAILED fetch is VISIBLE — `error` carries the message and `retry`
 * re-runs the same request — instead of a console.warn the operator cannot see, which left "no counts" and "the
 * counts request failed" indistinguishable on the screen.
 *
 * `fetcher` defaults to the real client and is injectable for tests; the real one serialises through
 * appendQueueFilterParams, the declaration the queue and Select All use (pinned in facetFilters.test.ts).
 */
export type FacetCountsFetcher = (filters: QueueFilters) => Promise<{ data: FacetCounts }>;

export function useFacetCounts(filters: QueueFilters, fetcher: FacetCountsFetcher = getQueueFacets) {
  const [counts, setCounts] = useState<FacetCounts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!filters.siteId) { setCounts(null); setError(null); return () => { cancelled = true; }; }
    // The previous counts belong to the previous filters or site: cleared at request start, so the button and the
    // summary never describe a population the operator is no longer looking at (PR #6169 follow-up, codex-reviewer-2
    // minor @acc7482d8). `counts === null && error === null` is the loading state.
    setCounts(null);
    setError(null);
    fetcher(filters)
      .then((r) => { if (!cancelled) setCounts(r.data); })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCounts(null);
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => { cancelled = true; };
  }, [filters, fetcher, attempt]);

  /** Re-issue the same request: the Retry button after a failure, and the dashboard's refresh path after a mutation
   *  (round 3, both legs minor @ca4646dba: an emptied queue kept its previous counts until the filters changed). */
  const refresh = useCallback(() => setAttempt((a) => a + 1), []);
  return { counts, error, retry: refresh, refresh };
}
